// The pure state machine behind `useAttachmentContent` — extracted so it can be unit-tested without
// dragging the data layer (React Query, the workspace fetchers) into the test graph. The hook computes
// these inputs from its three queries and calls this; `retry` is added there.

export interface AttachmentStateInputs {
  /** The attachment's own URL (trimmed). A URL attachment IS its content. */
  url: string;
  /** Whether the attachment is an agent-authored artifact addressed by a resolvable `workspaces/…` vfsPath. */
  hasRef: boolean;
  /** The whole-file-system (workspace-files list) resolution is still loading. */
  listPending: boolean;
  /** The content that resolution produced — empty when it settled to a miss. */
  listContent: string;
  /** The scoped single-file fallback (`/api/workspace-file`) is loading. */
  fallbackPending: boolean;
  /** The content the fallback produced — empty when it too found nothing. */
  fallbackContent: string;
}

export interface AttachmentContentState {
  content: string;
  pending: boolean;
  /** Both the list resolution AND the scoped fallback settled empty — the chip shows a retry, not a
   *  silent dead tap. */
  error: boolean;
}

/** URL wins; then the list resolution; then the scoped fallback; only when BOTH the list and the fallback
 *  have settled to nothing is it an error. A non-ref attachment (a plain URL, or an unresolvable path) is
 *  never an error — there is nothing to resolve.
 *
 *  ⚠️ ANY resolved CONTENT is authoritative over ANY PENDING — both queries' hits are checked before
 *  either query's pending. React Query keeps the previous `data` during a BACKGROUND refetch (`isFetching`
 *  true), so checking a pending flag before the OTHER source's content would flip an already-openable chip
 *  back to a spinner whenever either query refetched. So: list content, then fallback content, then (only
 *  with no content anywhere) list pending, then fallback pending, then error. A pending flag matters only
 *  while nothing has resolved yet (initial load, or a retry after a miss). */
export function resolveAttachmentContentState(i: AttachmentStateInputs): AttachmentContentState {
  if (i.url) return { content: i.url, pending: false, error: false };
  if (!i.hasRef) return { content: '', pending: false, error: false };
  if (i.listContent) return { content: i.listContent, pending: false, error: false };
  if (i.fallbackContent) return { content: i.fallbackContent, pending: false, error: false };
  if (i.listPending) return { content: '', pending: true, error: false };
  if (i.fallbackPending) return { content: '', pending: true, error: false };
  return { content: '', pending: false, error: true };
}
